import { useRouter } from "next/router"
import Link from "next/link"
import { useState, useRef, useEffect } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { getAutoStartToken, fetchQrCode, collectStatus } from "@/lib/api/member"
import { setMemberCookie } from "@/lib/helpers/member"
import { useMember } from "@/lib/hooks/useMember"
import { Spinner } from "@/components/common/loading/spinner"
import useBrowser from "@/lib/hooks/useBrowser"
import Close from "@/common/icons/close"
import Arrow from "@/common/icons/arrow"
import Block from "@/components/wrappers/block"
import Blocks from "@/components/Blocks"
import classNames from "classnames"
import styles from "./login.module.css"

export default function MemberLogin({ block }) {
  const recaptchaRef = useRef()
  const router = useRouter()
  const { hasMounted, isLoggedIn } = useMember()

  if (!hasMounted) {
    return (
      <Block block={block} width="full" className={styles.wrapper}>
        <div className="waiting-to-mount">
          <Spinner
            size="large"
            borderClass="border-green-100"
            spinColor="#ECF2CA"
            className="#DDDDDD"
          />
        </div>
      </Block>
    )
  }

  if (isLoggedIn) {
    router.reload()
  } else {
    return (
      <SelectLogin
        block={block}
        recaptchaRef={recaptchaRef}
        hasMounted={hasMounted}
      />
    )
  }
}

export function SelectLogin({ block, recaptchaRef, hasMounted }) {
  const siteKey = process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY

  const [qrImage, setQrImage] = useState()
  const [currentStep, setCurrentStep] = useState("loginForm")
  const [currentMethod, setCurrentMethod] = useState(false)
  const [error, setError] = useState(false)
  const router = useRouter()

  const imageTimer = useRef(null)
  const statusTimer = useRef(null)
  const imageFrequence = 10000
  const statusFrequence = 1000

  // Handler if API for some reason won't respond at all
  // or respond with a 500 error
  const severeError = () => {
    clearInterval(imageTimer.current)
    clearTimeout(statusTimer.current)
    setCurrentStep("loginForm")
    setCurrentMethod(false)
    setError("Ett fel uppstod vid inloggning. Försök igen.")
  }

  async function fetchTheQrCode(orderRef) {
    const captcha = await recaptchaRef.current.executeAsync()
    const response = await fetchQrCode(orderRef, captcha)

    if (!response || response.error === undefined) {
      severeError()
      return
    }

    const { error, message, image } = response

    if (error) {
      clearInterval(imageTimer.current)
      setCurrentStep("loginForm")
      setError(message)
      return
    }

    setQrImage(image)
  }

  async function collectAuthStatus(orderRef) {
    const captcha = await recaptchaRef.current.executeAsync()
    const response = await collectStatus(orderRef, captcha)

    if (!response || response.status === undefined) {
      severeError()
      return
    }

    const { status, message, token } = response

    switch (status) {
      case "error":
        clearInterval(imageTimer.current)
        clearTimeout(statusTimer.current)
        setCurrentStep("loginForm")
        setCurrentMethod(false)
        setError(message)
        return

      case "complete":
        clearInterval(imageTimer.current)
        clearTimeout(statusTimer.current)
        if (!token) {
          setCurrentStep("loginForm")
          setCurrentMethod(false)
          setError(message)
          return
        }
        setMemberCookie(token)
        router.reload()
        return

      case "signing":
        // User has now scanned QR or autoStartToken
        if (imageTimer.current) {
          clearInterval(imageTimer.current)
        }
        if (currentMethod === "other-device") {
          setCurrentStep("loading")
        }
        break
    }

    statusTimer.current = setTimeout(function () {
      collectAuthStatus(orderRef)
    }, statusFrequence)
  }

  async function loginSameDevice() {
    setError(false)
    setCurrentMethod("same-device")

    const captcha = await recaptchaRef.current.executeAsync()
    const response = await getAutoStartToken(captcha)

    if (!response || response.error === undefined) {
      severeError()
      return
    }

    const { error, message, token, ref } = response

    if (error) {
      setError(message)
      return
    }

    router.push(`bankid:///?autostarttoken=${token}&redirect=null`)
    collectAuthStatus(ref)
  }

  async function fetchAutoStartToken() {
    const captcha = await recaptchaRef.current.executeAsync()
    return await getAutoStartToken(captcha)
  }

  async function loginOtherDevice() {
    setError(false)
    setCurrentMethod("other-device")

    const captcha = await recaptchaRef.current.executeAsync()
    const response = await getAutoStartToken(captcha)

    if (!response || response.error === undefined) {
      severeError()
      return
    }

    const { error, message, ref } = response

    if (error) {
      setError(message)
      return
    }

    await fetchTheQrCode(ref)
    setCurrentStep("showingQr")

    imageTimer.current = setInterval(function () {
      fetchTheQrCode(ref)
    }, imageFrequence)

    collectAuthStatus(ref)
  }

  function cancelLogin() {
    clearInterval(imageTimer.current)
    clearTimeout(statusTimer.current)
    setCurrentStep("loginForm")
    setCurrentMethod(false)
  }

  const { heading, scanText, helpUrl } = block.attrs
  const settings = {
    "core/heading": { width: "bleed", innerAlign: "layout-align-left" },
    "core/paragraph": { width: "bleed", innerAlign: "layout-align-left" },
    "core/list": { width: "bleed", innerAlign: "layout-align-left" },
    "core/buttons": { width: "bleed", innerAlign: "layout-align-left" }
  }

  return (
    <Block block={block} width="full" className={styles.wrapper}>
      <h1>{heading}</h1>
      <div className="inner-wrapper">
        <div className="login">
          <ReCAPTCHA
            className="hidden"
            ref={recaptchaRef}
            size="invisible"
            sitekey={siteKey}
          />
          <h2>{block?.attrs?.loginMethod}</h2>
          <LoginForm
            block={block}
            currentStep={currentStep}
            currentMethod={currentMethod}
            cancelLogin={cancelLogin}
            loginSameDevice={loginSameDevice}
            loginOtherDevice={loginOtherDevice}
            fetchAutoStartToken={fetchAutoStartToken}
            collectAuthStatus={collectAuthStatus}
            hasMounted={hasMounted}
            recaptchaRef={recaptchaRef}
            error={error}
          />
          <CodeStep
            currentStep={currentStep}
            qrImage={qrImage}
            cancelLogin={cancelLogin}
            scanText={scanText}
            helpUrl={helpUrl}
          />
        </div>
        <div className="content">
          <Blocks blocks={block?.innerBlocks} settings={settings} />
        </div>
      </div>
    </Block>
  )
}

const LoginForm = ({
  block,
  currentStep,
  currentMethod,
  loginSameDevice,
  loginOtherDevice,
  cancelLogin,
  fetchAutoStartToken,
  hasMounted,
  recaptchaRef,
  collectAuthStatus,
  error
}) => {
  const browser = useBrowser()

  if (currentStep !== "loginForm") {
    return null
  }

  return (
    <>
      <InAppButton
        isInApp={browser.inApp}
        fetchAutoStartToken={fetchAutoStartToken}
        recaptchaRef={recaptchaRef}
        hasMounted={hasMounted}
        collectAuthStatus={collectAuthStatus}
        label={block?.attrs?.loginThisDevice ?? "Bank-id på samma enhet"}
      />
      <LoginMethodButton
        isInApp={browser.inApp}
        method="same-device"
        currentMethod={currentMethod}
        cancelCallback={cancelLogin}
        loginCallback={loginSameDevice}
        label={block?.attrs?.loginThisDevice ?? "Bank-id på samma enhet"}
      />
      <LoginMethodButton
        isInApp={browser.inApp}
        method="other-device"
        currentMethod={currentMethod}
        cancelCallback={cancelLogin}
        loginCallback={loginOtherDevice}
        label={block?.attrs?.loginOtherDevice ?? "Bank-id på samma enhet"}
      />
      {error && (
        <div className="error" dangerouslySetInnerHTML={{ __html: error }} />
      )}
    </>
  )
}

const CodeStep = ({ currentStep, qrImage, cancelLogin, scanText, helpUrl }) => {
  if (currentStep !== "showingQr") {
    return null
  }

  return (
    <div className="other-device-step">
      <a className="cancel" onClick={() => cancelLogin()}>
        <Arrow className="w-6 h-6" />
        Byt inloggningssätt
      </a>
      <p>{scanText}</p>
      {helpUrl && (
        <Link href={helpUrl} className="help">
          Jag behöver hjälp
        </Link>
      )}
      <figure>
        <img src={qrImage} />
      </figure>
    </div>
  )
}

const LoginMethodButton = ({
  isInApp,
  method,
  currentMethod,
  label,
  cancelCallback,
  loginCallback
}) => {
  const isDisabled = !method || (currentMethod && method !== currentMethod)
  const inProgress = currentMethod && method === currentMethod

  if (isInApp) {
    return null
  }

  return (
    <button
      disabled={isDisabled}
      aria-label={label}
      className={classNames("login-method", {
        disabled: isDisabled,
        "in-progress": inProgress
      })}
      onClick={() => {
        !inProgress && loginCallback()
        inProgress && cancelCallback()
      }}>
      <span>{label}</span>
      <div className="square">
        {!inProgress && <Arrow className="w-6 h-6" />}
        {inProgress && (
          <>
            <Spinner
              className="spinner"
              size="small"
              borderClass="border-black"
              spinColor="rgb(185, 210, 50)"
            />
            <Close className="close" />
          </>
        )}
      </div>
    </button>
  )
}

const InAppButton = ({
  isInApp,
  label,
  fetchAutoStartToken,
  hasMounted,
  recaptchaRef,
  collectAuthStatus
}) => {
  const [token, setToken] = useState(null)
  const [ref, setRef] = useState(null)
  const [loading, setLoading] = useState(false)
  const [inProgress, setInProgress] = useState(false)

  useEffect(() => {
    if (!hasMounted || loading || !isInApp) {
      return
    }

    setLoading(true)
    fetchAutoStartToken().then((response) => {
      if (response) {
        setToken(response.token)
        setRef(response.ref)
        setLoading(false)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMounted, recaptchaRef.current])

  if (!hasMounted || !isInApp) {
    return null
  }

  if (loading || !token) {
    return (
      <div className="login-method">
        <span>Var god vänta...</span>
        <div className="square">
          <Spinner
            className="spinner"
            size="small"
            borderClass="border-black"
            spinColor="rgb(185, 210, 50)"
          />
        </div>
      </div>
    )
  }

  return (
    <a
      href={`https://app.bankid.com/?autostarttoken=${token}&redirect=null`}
      onClick={(e) => {
        if (inProgress) {
          e.preventDefault()
          return
        }

        setInProgress(true)
        collectAuthStatus(ref)
      }}
      className="login-method">
      <span>{label}</span>
      <div className="square">
        {!inProgress && <Arrow className="w-6 h-6" />}
        {inProgress && (
          <>
            <Spinner
              className="spinner"
              size="small"
              borderClass="border-black"
              spinColor="rgb(185, 210, 50)"
            />
            <Close className="close" />
          </>
        )}
      </div>
    </a>
  )
}
